<template>
<div>
    <v-card color="transparent" flat>
        <v-toolbar color="transparent" flat>
            <v-toolbar-title class="font-weight-bold">{{ $t('geneticsAndBreeding.animalDetails') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="$router.push({name: 'GeneticsList'})">{{ $t('geneticsAndBreeding.cancel') }}</v-btn>
            <v-btn color="primary" class="ml-2" depressed @click="submitForm"> {{ action == 'create'? $t('submit') : $t('update')}}</v-btn>
        </v-toolbar>
    </v-card>
    <v-card class="mt-5 d-flex justify-space-between align-center mb-6" flat tile>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-avatar size="90" class="pa-0">
                        <v-img :src="animalImg"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.animalID') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.animalNumber || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.tag') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            # {{ animal.tagNumber || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat class="align-center" max-width="150">
            <v-list>
                <v-list-item>
                    <v-list-item-content class="text-center">
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.dateOfBirth') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.dob || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.gender') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.gender || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.breed') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.breed || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.regDate') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.createdAt || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
        <v-divider vertical inset></v-divider>
        <v-card flat>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-list-item-subtitle>
                            {{ $t('geneticsAndBreeding.status') }}
                        </v-list-item-subtitle>
                        <v-list-item-title class="font-weight-bold mt-2 text-capitalize">
                            {{ animal.status || 'N/A'}}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
    </v-card>
    <v-form ref="form" v-model="valid" lazy-validation>
        <v-card class="px-5">
            <v-toolbar flat>
                <v-toolbar-title>{{ action == 'create'? $t('geneticsAndBreeding.add') : $t('geneticsAndBreeding.update')}} {{ $t('geneticsAndBreeding.dataForEPDs') }}</v-toolbar-title>
                <v-spacer></v-spacer>
            </v-toolbar>
            <v-divider></v-divider>
            <v-card-text>
                <v-expansion-panels flat v-model="panel">
                    <v-expansion-panel class="mb-2" flat>
                        <v-expansion-panel-header disable-icon-rotate class="rounded-lg font-weight-bold" :class="[panel===0 ? 'expansion-header' : 'expansion-close']">
                            {{ $t('geneticsAndBreeding.basicInformation') }}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="pt-5">
                            <v-row>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{ $t('geneticsAndBreeding.internationalID') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.internationalID') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field  dense outlined :placeholder="$t('geneticsAndBreeding.enterID')"  v-model="epd.internationalId" maxlength="20"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{ $t('geneticsAndBreeding.animalRegistration') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.regNo') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="animal.animalNumber" disabled></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{ $t('geneticsAndBreeding.location') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.location') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.location" maxlength="25"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{ $t('geneticsAndBreeding.animalName') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.animalName') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="animal.name" :error-messages="errors.name" maxlength="20"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="gender">
                                        {{$t("animal.gender")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.gender') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="genders" item-text="text" item-value="value" outlined dense v-model="animal.gender" :error-messages="errors.gender"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{ $t('geneticsAndBreeding.additionalIDMethod') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.additionalIDMethod') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="animal.uniqueIdentifier" :error-messages="errors.uniqueIdentifier" maxlength="20"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">{{$t("animal.conceptionMethod")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.conceptionMethod') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="conceptionMethod" item-text="text" item-value="value" outlined dense v-model="animal.conceptionMethod" :error-messages="errors.conceptionMethod"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{ $t('geneticsAndBreeding.damID') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.damID') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <AnimalDropdown
                                        v-model="animal.damPubId"
                                        animalType="dam"
                                        :currentAnimal="$route.params.geneticid || null"
                                    />
                                    <!-- <v-text-field dense outlined  v-model="animal.damPubId" :error-messages="errors.damPubId" maxlength="20"></v-text-field> -->
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{ $t('geneticsAndBreeding.sireID') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.sireId') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <AnimalDropdown v-model="animal.sirePubId" animalType="sire" :currentAnimal="$route.params.geneticid || null"/>
                                    <!-- <v-text-field dense outlined v-model="animal.sirePubId" :error-messages="errors.sirePubId" maxlength="20"></v-text-field> -->
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("animal.dateOfBirth")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.dateOfBirth') }}</span>
                                        </v-tooltip>

                                    </label>
                                    <v-menu v-model="animalDOBmenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="animalDOBmenu = true" append-icon="mdi-calendar" outlined dense readonly v-bind="attrs" v-on="on" v-model="animal.dob" :error-messages="errors.dob"></v-text-field>
                                        </template>
                                        <v-date-picker 
                                            v-model="animal.dob" 
                                            :max="today"
                                            @input="animalDOBmenu = false">
                                        </v-date-picker>
                                    </v-menu>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel class="mb-2" flat>
                        <v-expansion-panel-header disable-icon-rotate class="rounded-lg font-weight-bold" :class="[panel===1 ? 'expansion-header' : 'expansion-close']">
                            {{ $t('geneticsAndBreeding.birthWeightInformation') }}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="pt-5">
                            <v-row>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{ $t('geneticsAndBreeding.breederHerdCode') }}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.breederHerdCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined disabled v-model="animalEPD.breeder_code"></v-text-field>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.birthYear")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.birthYear') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-menu v-model="birthYearMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field class="required-field" @click:append="birthYearMenu = true" append-icon="mdi-calendar" flat solo dense readonly v-bind="attrs" v-on="on" v-model="birthYear" disabled></v-text-field>
                                        </template>
                                        <v-date-picker v-model="birthYear" @input="birthYearMenu = false" type="year"></v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <v-row>
                                        <v-col cols="6">
                                            <label class="subtitle-1 font-weight-medium text-no-wrap">
                                                {{$t("geneticsAndBreeding.primaryBreed")}}
                                                <v-tooltip top color="black" max-width="350">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('epd.primaryBreed') }}</span>
                                                </v-tooltip>
                                            </label>
                                            <v-select outlined dense :items="breeds"> </v-select>
                                        </v-col>
                                        <v-col cols="6">
                                            <label class="subtitle-1 font-weight-medium text-no-wrap">
                                                {{$t("geneticsAndBreeding.breedComposition")}}
                                                <v-tooltip top color="black" max-width="350">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('epd.breedComposition') }}</span>
                                                </v-tooltip>
                                            </label>
                                            <v-text-field max="100" v-model="breedComposition1" @keypress="NumbersOnly" outlined dense type="number" min="0"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <label class="subtitle-1 font-weight-medium">
                                                {{$t("geneticsAndBreeding.secondaryBreed")}}
                                                <v-tooltip top color="black" max-width="350">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('epd.secondaryBreed') }}</span>
                                                </v-tooltip>
                                            </label>
                                            <v-select outlined dense :items="breeds">
                                            </v-select>
                                        </v-col>
                                        <v-col cols="6">
                                            <label class="subtitle-1 font-weight-medium">
                                                {{$t("geneticsAndBreeding.breedComposition")}}
                                                <v-tooltip top color="black" max-width="350">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on">
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>{{ $t('epd.breedComposition') }}</span>
                                                </v-tooltip>
                                            </label>
                                            <v-text-field v-model="breedCompositionSecondary1" @keypress="NumbersOnly" outlined dense type="number" min="0" max="100"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{$t("epd.seasonofBirth")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.seasonofBirth') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select :items="['January-June', 'July-December']" item-text="text" item-value="value" outlined dense v-model="epd.season"></v-select>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.birthManagementCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.birthManagementCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.managementCode"></v-text-field>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{$t("geneticsAndBreeding.damAge")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.damAge') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field @keypress="NumbersOnly" :rules="formRules.damAge" v-model="epd.ageOfDam" outlined dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select outlined dense :items="['Years']" value="Years">
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">
                                        {{$t("animal.birthWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.birthWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field class="required-field" @keypress="NumbersOnly" :rules="formRules.birthWeight" v-model="epd.birthWeight" solo flat dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select append-icon="" class="required-field" solo flat dense :items="weightUnits" item-text="name" item-value="value" :value="epd.unit" disabled>
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium text-no-wrap">
                                        {{$t("geneticsAndBreeding.birthContemporaryCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.birthContemporaryCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.birthWeightContGrp"></v-text-field>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">{{$t("animal.conceptionMethod")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.conceptionMethod') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="conceptionMethod" item-text="text" item-value="value" class="required-field" solo flat dense v-model="animal.conceptionMethod"></v-select>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="gender">
                                        {{$t("animal.gender")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.gender') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" class="required-field" :items="genders" item-text="text" item-value="value" solo flat dense v-model="animal.gender"></v-select>
                                </v-col>
                                <v-col lg="4" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="accuracyWeight_for_field">
                                        {{$t("geneticsAndBreeding.accuracyWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.accuracyWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field outlined dense v-model="epd.accuracyWeight_for_field" @keypress="NumbersOnly"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-alert text outlined color="secondary2" class="note">
                                <span class="black--text"><b>{{ $t('geneticsAndBreeding.note') }}:</b> {{ $t('geneticsAndBreeding.minimumRequiredFieldForBirthWeight') }} <b>{{ $t('geneticsAndBreeding.birthYearGenderConceptionMethodBirthWeight') }}</b>.</span>
                            </v-alert>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel class="mb-2" flat>
                        <v-expansion-panel-header disable-icon-rotate class="rounded-lg font-weight-bold" :class="[panel===2 ? 'expansion-header' : 'expansion-close']">
                            {{$t("geneticsAndBreeding.weaningWeightInformation")}}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="pt-5">
                            <v-row>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">{{$t("geneticsAndBreeding.weaningWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.weaningWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field v-model="epd.weaningWeight" @keypress="NumbersOnly" class="required-field" solo flat dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select append-icon="" class="required-field" solo flat dense :items="weightUnits" item-text="name" item-value="value" :value="epd.unit" disabled>
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.weaningDate")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.weaningDate') }}</span>
                                        </v-tooltip>

                                    </label>
                                    <v-menu v-model="animalWeaningDatemenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="animalWeaningDatemenu = true" append-icon="mdi-calendar" class="required-field" solo flat dense readonly v-bind="attrs" v-on="on" v-model="epd.weaningDate"></v-text-field>
                                        </template>
                                        <v-date-picker v-model="epd.weaningDate" @input="animalWeaningDatemenu = false"></v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium text-no-wrap">
                                        {{$t("geneticsAndBreeding.weaningContemporaryGroupCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.weaningContemporaryGroupCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.weaningWeightContGrp"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="Creep">
                                        {{$t("geneticsAndBreeding.creep")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.creep') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select :items="[{text: 'Yes', value: 'yes'}, {text: 'No', value: 'no'}]" item-text="text" item-value="value" outlined dense v-model="epd.creep"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.managementPastureCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.managementPastureCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense class="required-field" solo flat v-model="epd.pastureCode"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">{{$t("animal.conceptionMethod")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.conceptionMethod') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="conceptionMethod" item-text="text" item-value="value" outlined dense v-model="animal.conceptionMethod"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="gender">
                                        {{$t("geneticsAndBreeding.weaningSex")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.weaningSex') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="genders" item-text="text" item-value="value" class="required-field" solo flat dense v-model="animal.gender"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="accuracyWeight_for_field">
                                        {{$t("geneticsAndBreeding.accuracyWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.accuracyWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field outlined dense v-model="epd.accuracyWeight_for_field" @keypress="NumbersOnly"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-alert text outlined color="secondary2" class="note">
                                <span class="black--text"><b>{{ $t('geneticsAndBreeding.note') }}:</b> {{ $t('geneticsAndBreeding.minimumRequiredFieldForWeaningWeight') }} <b>{{ $t('geneticsAndBreeding.weaningWeightManagementPastureCodeWeaningSexDateWeighed') }}</b>.</span>
                            </v-alert>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel class="mb-2" flat>
                        <v-expansion-panel-header disable-icon-rotate class="rounded-lg font-weight-bold" :class="[panel===3 ? 'expansion-header' : 'expansion-close']">
                            {{$t("geneticsAndBreeding.yearlingWeightInformation")}}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="pt-5">
                            <v-row>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="conceptionMethod">{{$t("geneticsAndBreeding.yearlingWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.yearlingWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field v-model="epd.yearlingWeight" @keypress="NumbersOnly" outlined dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select append-icon="" outlined dense :items="weightUnits" item-text="name" item-value="value" :value="epd.unit" disabled>
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">{{$t("geneticsAndBreeding.yearlingHeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.yearlingHeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field :rules="formRules.yearlingHeight" @keypress="NumbersOnly" v-model="epd.yearlingHeight" outlined dense type="number" min="0" max="84"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select outlined dense :items="['Inch']" value="Inch">
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.yearlingDate")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.yearlingDate') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-menu v-model="yearlingDatemenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="yearlingDatemenu = true" append-icon="mdi-calendar" outlined dense readonly v-bind="attrs" v-on="on" v-model="epd.yearlingDate"></v-text-field>
                                        </template>
                                        <v-date-picker v-model="epd.yearlingDate" @input="yearlingDatemenu = false"></v-date-picker>
                                    </v-menu>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium text-no-wrap">
                                        {{$t("geneticsAndBreeding.yearlingContemporaryGroupCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.yearlingWeightContemporaryGroupCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.yearlingWeightContGrp"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium text-no-wrap">
                                        {{$t("geneticsAndBreeding.managementFeedingCode")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.managementFeedUnitCode') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.feedingCode"></v-text-field>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="gender">
                                        {{$t("geneticsAndBreeding.yearlingSex")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.yearlingSex') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="genders" item-text="text" item-value="value" outlined dense v-model="animal.gender"></v-select>
                                </v-col>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="accuracyWeight_for_field">
                                        {{$t("geneticsAndBreeding.accuracyWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.accuracyWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field outlined dense v-model="epd.accuracyWeight_for_field" @keypress="NumbersOnly"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-alert text outlined color="secondary2">
                                <span class="black--text"><b>{{ $t('geneticsAndBreeding.note') }}:</b> {{ $t('geneticsAndBreeding.youWillNeedToFillOutFor') }} <b>{{ $t('geneticsAndBreeding.yearlingWeight') }}</b>.</span>
                            </v-alert>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                    <v-expansion-panel class="mb-2" flat>
                        <v-expansion-panel-header disable-icon-rotate class="rounded-lg font-weight-bold" :class="[panel===4 ? 'expansion-header' : 'expansion-close']">
                            {{$t("geneticsAndBreeding.carcassWeightInformation")}}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="pt-5">
                            <p v-if="epd.harvestDate==''" class="font-weight-medium red--text">{{$t("geneticsAndBreeding.pleaseSelectHavestDateFirstToEnterInfo")}}</p>
                            <v-row>
                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.harvestDate")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.harvestDate') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-menu v-model="harvestDateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="harvestDateMenu = true" append-icon="mdi-calendar" outlined dense readonly v-bind="attrs" v-on="on" v-model="epd.harvestDate" :error-messages="errors.harvestDate"></v-text-field>
                                        </template>
                                        <v-date-picker v-model="epd.harvestDate" @input="harvestDateMenu = false"></v-date-picker>
                                    </v-menu>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.dateOnFeed")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.dateonFeed') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-menu v-model="dateOnFeedMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="dateOnFeedMenu = true" append-icon="mdi-calendar" outlined dense readonly v-bind="attrs" v-on="on" v-model="epd.dateOnFeed"></v-text-field>
                                        </template>
                                        <v-date-picker v-model="epd.dateOnFeed" @input="dateOnFeedMenu = false"></v-date-picker>
                                    </v-menu>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.daysOnFeed")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.daysOnFeed') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.daysOnFeed" @keypress="NumbersOnly" type="number" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="dob">{{$t("geneticsAndBreeding.gradingDate")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.gradingDate') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-menu v-model="gradingDateMenu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field @click:append="gradingDateMenu = true" append-icon="mdi-calendar" outlined dense readonly v-bind="attrs" v-on="on" v-model="epd.gradingDate"></v-text-field>
                                        </template>
                                        <v-date-picker v-model="epd.gradingDate" @input="gradingDateMenu = false"></v-date-picker>
                                    </v-menu>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.carcassSex")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.carcassSex') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-select append-icon="" :items="genders" item-text="text" item-value="value" outlined dense v-model="animal.gender"></v-select>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="CarcassWeight">{{$t("geneticsAndBreeding.carcassWeight")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.carcassWeight') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field v-model="epd.carcassWeight" :disabled="epd.harvestDate==''" @keypress="NumbersOnly" outlined dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select append-icon="" outlined dense :items="weightUnits" item-text="name" item-value="value" :value="epd.unit" disabled>
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.carcassContemporary")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.carcassContemporary")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.carcassWeightContGrp" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.damBreed")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.damBreed")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.breedOfDam"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.ribEyeArea")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.ribeyeArea') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.ribEyeArea" @keypress="NumbersOnly" type="number" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.ribEyeAreaContemporary")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.ribEyeAreaContemporary")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.ribEyeArea_contemporary" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.fatThickness")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.fatThickness') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.fatThickness" @keypress="NumbersOnly" type="number" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.fatThicknessContemporary")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.fatThicknessContemporary")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.fatThickness_contemporary" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.marbling")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.marbling') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.marbling" @keypress="NumbersOnly" type="number" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.marblingContemporary")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.marblingContemporary")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.marblingContGrp" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium" for="CarcassWeight">{{$t("KPH Fat")}} 
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{ $t('epd.KPH') }}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-row class="d-flex">
                                        <v-col cols="6">
                                            <v-text-field v-model="epd.kphFat" @keypress="NumbersOnly" outlined dense type="number" min="0"></v-text-field>
                                        </v-col>
                                        <v-col cols="6">
                                            <v-select append-icon="" outlined dense :items="['%']" :value="'%'" disabled>
                                            </v-select>
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <!-- 
                                <v-col  lg="3" md="6" sm="6" xs="6">
                                    <label
                                        class="subtitle-1 font-weight-medium"
                                    >
                                    Yield Grade
                                    <v-tooltip top color="black" max-width="350">
                                        <template v-slot:activator="{ on, attrs }">
                                        <v-icon size="20" v-bind="attrs" v-on="on">
                                            mdi-information-outline
                                        </v-icon>
                                        </template>
                                        <span>Yield Grade</span>
                                    </v-tooltip>
                                    </label>
                                    <v-text-field
                                        dense
                                        outlined
                                        v-model="epd.yieldGrade"
                                        @keypress="NumbersOnly"
                                        type="number"
                                        :disabled="epd.harvestDate==''"
                                    ></v-text-field>
                                </v-col> -->

                                <v-col lg="3" md="6" sm="6" xs="6">
                                    <label class="subtitle-1 font-weight-medium">
                                        {{$t("geneticsAndBreeding.yieldGradeContemporary")}}
                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon v-bind="attrs" v-on="on">
                                                    mdi-information-outline
                                                </v-icon>
                                            </template>
                                            <span>{{$t("geneticsAndBreeding.yieldGradeContemporary")}}</span>
                                        </v-tooltip>
                                    </label>
                                    <v-text-field dense outlined v-model="epd.yieldGradeContGrp" :disabled="epd.harvestDate==''"></v-text-field>
                                </v-col>

                            </v-row>
                            <v-alert text outlined color="secondary2">
                                <span class="black--text"><b>{{ $t('geneticsAndBreeding.note') }}:</b> {{ $t('geneticsAndBreeding.youWillNeedToFillOutFor') }} <b>{{ $t('geneticsAndBreeding.carcassWeight') }}</b>.</span>
                            </v-alert>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card-text>
        </v-card>
    </v-form>
    <v-dialog v-model="dataSubType" persistent max-width="400">
        <v-card class="pb-5">
            <v-toolbar flat class="white--text" color="secondary">
                <v-toolbar-title>Submitting Data...</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="dataSubType=false">
                    <v-icon class="white--text">mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="pt-5 text-center">
                <v-img height="130" contain src="/img/shield.png"></v-img>
                <p class="mt-5"> {{ errorMsg }}</p>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" outlined @click="dataSubType=false">
                    Cancel

                </v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <!-- <v-dialog v-model="mainDataWarning" persistent max-width="400">
        <v-card class="pb-5">
            <v-toolbar flat class="white--text" color="secondary">
                <v-toolbar-title></v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="mainDataWarning=false">
                    <v-icon class="white--text">mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="pt-5">
                <h4 class="black--text">Are you sure?</h4>
                <p class="mt-5">This will update your main animal info.</p>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" outlined @click="mainDataWarning=false">
                    Cancel

                </v-btn>
                <v-btn color="primary" @click="()=> {mainDataWarning=false; editMainData=true}">
                    Confirm
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog> -->
</div>
</template>

<script>
import EPDService from "@/_services/EPDService";
import loadingMixin from "@/mixins/LoadingMixin";
import AnimalService from "@/_services/AnimalService";
import AnimalDropdown from "@/components/AnimalDropdown.vue";
import {
    breeds
} from "@/models/units.js"

export default {
    components: {
        AnimalDropdown,
    },
    mounted() {
        if (this.action == 'update') {
            this.assignValues()
        }
        if (this.action == 'create') {
            this.epd.unit = this.animal.weightUnit;
            this.epd.birthWeight = this.animal.birthWeight;
        }
        this.breedComposition1 = this.epd.breedComposition
        this.breedCompositionSecondary1 = this.epd.breedCompositionSecondary
        // this.editMainDataFun
        // this.pythonUrl = process.env.VUE_APP_PYTHON_BACKEND
        // console.log('py', this.pythonUrl, process.env.VUE_APP_PYTHON_BACKEND)
    },

    watch: {
        epd: {
            handler() {
                // this.$emit('form-updated', false);
            },
            deep: true
        },
        animal: {
            handler() {
                if (this.action == 'update') {
                    this.assignValues()
                }
            }
        },
        'epd.harvestDate': {
            handler() {
                this.calculateDaysOnFeed()
            },
            deep: true
        },
        'epd.daysOnFeed': {
            handler() {
                this.calculateDaysOnFeed()
            },
            deep: true
        },
        'epd.dateOnFeed': {
            handler() {
                this.calculateDaysOnFeed()
            },
            deep: true
        },
        breedComposition1: function (val) {
            if (val < 101) {
                this.breedCompositionSecondary1 = 100 - val
            } else {
                this.breedComposition1 = 0
            }
        },
        breedCompositionSecondary1: function (val) {
            if (val < 101) {
                this.breedComposition1 = 100 - val
            } else {
                this.breedCompositionSecondary1 = 0
            }
        },
    },
    computed: {
        birthYear() {
            return this.animal.dob == '' ? '' : new Date(this.animal.dob).getFullYear().toString();
        },
        animalImg() {
            return this.animal?.img?.url || '/img/no_img.png'
        },
        today() {
            return new Date().toISOString().split('T')[0];
        }
    },
    data() {
        return {
            breeds,
            pythonUrl: "",
            ele: "",
            valid: true,
            formRules: {
                damAge: [
                    // v => !!v || 'Dam age is required.',
                    v => (!v || v > 0) || "Can't be less then 1",
                ],
                birthWeight: [
                    // v => !!v || 'Birth weight is required',
                    v => (!v || v > 0) || "Can't be less then 1",
                ],
                yearlingHeight: [
                    // v => (v == "" || (v > 0 && v <= 84) || "Hight should be between 1-84"),
                ]
            },
            mainDataWarning: false,
            editMainData: true,
            dataSubType: false,
            errorMsg: '',
            breedComposition1: "",
            breedCompositionSecondary1: "",
            epd: {
                animalRegistrationId: null,
                location: "",
                ageOfDam: "",
                birthWeight: "",
                weaningDate: "",
                weaningWeight: "",
                yearlingDate: "",
                yearlingWeight: "",
                yearlingHeight: "",
                internationalId: "",
                pastureCode: "",
                feedingCode: "",
                managementCode: "",
                breedComposition: "",
                breedCompositionSecondary: "",
                serviceType: "",
                season: "",
                creep: "no",
                birthWeightContGrp: "",
                weaningWeightContGrp: "",
                yearlingWeightContGrp: "",
                unit: "LBS",
                accuracyWeight_for_field: null,
                daysOnFeed: "",
                harvestDate: "",
                gradingDate: "",
                dateOnFeed: "",
                breedOfDam: "",
                ribEyeArea: "",
                ribEyeArea_contemporary: "",
                fatThickness: "",
                fatThickness_contemporary: "",
                marbling: "",
                carcassWeight: "",
                marblingContGrp: "",
                kphFat: "",
                yieldGradeContGrp: "",
            },
            animalEPD: {},
            animalDOBmenu: false,
            harvestDateMenu: false,
            gradingDateMenu: false,
            dateOnFeedMenu: false,
            birthYearMenu: false,
            animalWeaningDatemenu: false,
            yearlingDatemenu: false,
            panel: 0,
            genders: [{
                    text: this.$t("animal.male"),
                    value: "male"
                },
                {
                    text: this.$t("animal.female"),
                    value: "female"
                },
            ],
            conceptionMethod: [{
                    text: this.$t("animal.naturalMating"),
                    value: "1"
                },
                {
                    text: this.$t("animal.artificialInsemination"),
                    value: "2"
                },
                {
                    text: this.$t("animal.embryoTransfer"),
                    value: "3"
                },
            ],
            weightUnits: [{
                    name: 'Kg',
                    value: 'KG'
                },
                {
                    name: 'Lbs',
                    value: 'LBS'
                }
            ],
            switch1: false,
            errors: {
                registrationNumber: "",
                birthWeight: "",
                sirePubId: "",
                damPubId: "",
                conceptionMethod: "",
                gender: "",
                dob: "",
                name: "",
                harvestDate: ""
            },
        }
    },
    props: {
        animal: {
            required: true
        },
        action: {
            type: String,
            default: 'create' // accepted values : create, update
        },
    },
    methods: {
        closeWarningMessage(stay) {
            this.dataLossWarning = false;
            if (!stay) {
                this.formChanged = false;
                this.$router.push(this.promptToRoute)
            }
        },
        NumbersOnly(evt) {
            evt = (evt) ? evt : window.event;
            var charCode = (evt.which) ? evt.which : evt.keyCode;
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
                evt.preventDefault();
            } else {
                return true;
            }
        },
        submitForm() {
            if (!this.$refs.form.validate()) return;
            this.$emit('form-updated', false)
            this.epd.animalRegistrationId = this.animal.id;
            this.epd.ageOfDam = Number(this.epd.ageOfDam)
            this.epd.birthWeight = Number(this.epd.birthWeight)
            this.epd.weaningWeight = Number(this.epd.weaningWeight)
            this.epd.yearlingWeight = Number(this.epd.yearlingWeight)
            this.epd.yearlingHeight = Number(this.epd.yearlingHeight)
            this.epd.kphFat = Number(this.epd.kphFat)
            this.epd.fatThickness = Number(this.epd.fatThickness)
            this.epd.ribEyeArea = Number(this.epd.ribEyeArea)
            this.epd.marbling = Number(this.epd.marbling)
            this.epd.carcassWeight = Number(this.epd.carcassWeight)
            this.epd.accuracyWeight_for_field = this.epd.accuracyWeight_for_field == null ? null : Number(this.epd.accuracyWeight_for_field)

            for (const [key, value] of Object.entries(this.epd)) {
                // console.log(`${key}: ${value}`);
                if (!value || value == '' || value == null) {
                    delete this.epd[key];
                }
            }

            this.updateAnimal()

            // if (this.action == 'create')
            //     this.createEPDrecord()
            // else if (this.action == 'update')
            //     this.updateEPD()
        },
        editMainDataFun() {
            // if(this.editMainData) this.editMainData= false;
            // else this.mainDataWarning = true;
        },
        updateEPD() {
            this.startLoading()
            EPDService.updateEPDrecordV1(this.epd)
                .then((res) => {
                    if (!res.success) throw res.message
                    // if (this.editMainData) {
                    //     this.stopLoading()
                    //     this.updateAnimal()
                    // } else {
                        this.$notify({
                            title: "Record update successfully.",
                            text: res.message,
                            type: "success",
                        });
                        this.stopLoading()
                        this.$router.push({
                            name: 'SingleAnimalEpd',
                            params: {
                                geneticid: this.animal.animalNumber
                            }
                        })

                    // }
                })
                .catch((err) => {
                    if (typeof err === 'string' || err instanceof String) {
                        this.errorMsg = err
                    } else {
                        this.errorMsg = err?.data?.message || 'Something went wrong! Please try after sometime.'
                    }
                    this.dataSubType = true
                    this.stopLoading()
                })
        },
        updateAnimal() {
            this.startLoading()
            AnimalService.updateAnimal(this.animal)
                .then(() => {
                    this.stopLoading()
                    if (this.action == 'create')
                        this.createEPDrecord()
                    else if (this.action == 'update')
                        this.updateEPD()
                    // this.$router.push({
                    //     name: 'SingleAnimalEpd',
                    //     params: {
                    //         geneticid: this.animal.animalNumber
                    //     }
                    // })
                })
                .catch(() => {
                    this.stopLoading()
                    if (this.action == 'create')
                        this.createEPDrecord()
                    else if (this.action == 'update')
                        this.updateEPD()
                    // this.$router.push({
                    //     name: 'SingleAnimalEpd',
                    //     params: {
                    //         geneticid: this.animal.animalNumber
                    //     }
                    // })
                });
        },
        async calculateDaysOnFeed() {
            if (this.epd.harvestDate != '' && this.epd.dateOnFeed != '') {
                const date1 = new Date(this.epd.dateOnFeed);
                const date2 = new Date(this.epd.harvestDate);
                if (this.epd.harvestDate < this.epd.dateOnFeed) {
                    this.errors.harvestDate = "Can't less then Date On Feed"
                    this.epd.daysOnFeed = ""
                    return
                } else
                    this.errors.harvestDate = ""

                const diffTime = await Math.abs(date2 - date1);
                // const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                this.epd.daysOnFeed = await Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
        },
        createEPDrecord() {
            this.startLoading()
            EPDService.createEPDrecordV1(this.epd)
                .then((res) => {
                    this.stopLoading()
                    if (!res.success) throw res.message
                    // if (this.editMainData) {
                    //     this.updateAnimal()
                    // } else {
                        this.$notify({
                            title: "Record created successfully.",
                            text: res.message,
                            type: "success",
                        });
                        this.$router.push({
                            name: 'SingleAnimalEpd',
                            params: {
                                geneticid: this.animal.animalNumber
                            }
                        })
                    // }
                })
                .catch((err) => {
                    this.stopLoading()
                    if (typeof err === 'string' || err instanceof String) {
                        this.errorMsg = err
                    } else {
                        this.errorMsg = err?.data?.message || 'Something went wrong! Please try after sometime.'
                    }
                    this.dataSubType = true
                })
        },
        assignValues() {
            if (this.animal?.AnimalEpd) {
                this.animalEPD = {
                    ...this.animal.AnimalEpd
                }
                const epdDD = {
                    ...this.animal.AnimalEpd
                }
                this.epd.animalRegistrationId = this.animal.id,
                    this.epd.location = epdDD.location ? epdDD.location : this.epd.location
                this.epd.ageOfDam = epdDD.age_of_dam ? epdDD.age_of_dam : this.epd.ageOfDam
                this.epd.birthWeight = epdDD.birth_weight ? epdDD.birth_weight : this.epd.birthWeight
                this.epd.weaningDate = epdDD.weaning_date ? epdDD.weaning_date : this.epd.weaningDate
                this.epd.weaningWeight = epdDD.weaning_weight ? epdDD.weaning_weight : this.epd.weaningWeight
                this.epd.yearlingDate = epdDD.yearling_date ? epdDD.yearling_date : this.epd.yearlingDate
                this.epd.yearlingWeight = epdDD.yearling_weight ? epdDD.yearling_weight : this.epd.yearlingWeight
                this.epd.yearlingHeight = epdDD.yearling_height ? epdDD.yearling_height : this.epd.yearlingHeight
                this.epd.internationalId = epdDD.international_id ? epdDD.international_id : this.epd.internationalId
                this.epd.pastureCode = epdDD.pasture_code ? epdDD.pasture_code : this.epd.pastureCode
                this.epd.feedingCode = epdDD.feeding_code ? epdDD.feeding_code : this.epd.feedingCode
                this.epd.managementCode = epdDD.management_code ? epdDD.management_code : this.epd.managementCode
                this.epd.breedComposition = epdDD.breed_composition ? epdDD.breed_composition : this.epd.breedComposition
                this.epd.breedCompositionSecondary = epdDD.breed_composition_secondary ? epdDD.breed_composition_secondary : this.epd.breedCompositionSecondary
                this.epd.season = epdDD.season ? epdDD.season : this.epd.season
                this.epd.creep = epdDD.creep ? epdDD.creep : this.epd.creep
                this.epd.weaningWeightContGrp = epdDD.weaning_contemporary ? epdDD.weaning_contemporary : this.epd.weaningWeightContGrp
                this.epd.yearlingWeightContGrp = epdDD.yearling_contemporary ? epdDD.yearling_contemporary : this.epd.yearlingWeightContGrp
                this.epd.accuracyWeight_for_field = epdDD.accuracyWeight_for_field ? epdDD.accuracyWeight_for_field : this.epd.accuracyWeight_for_field
                this.epd.unit = this.animal.weightUnit;
                this.breedComposition1 = this.epd.breedComposition
                this.breedCompositionSecondary1 = this.epd.breedCompositionSecondary
                this.epd.daysOnFeed = epdDD.daysOnFeed ? epdDD.daysOnFeed : this.epd.daysOnFeed
                this.epd.harvestDate = epdDD.harvestDate ? epdDD.harvestDate : this.epd.harvestDate
                this.epd.gradingDate = epdDD.gradingDate ? epdDD.gradingDate : this.epd.gradingDate
                this.epd.dateOnFeed = epdDD.dateOnFeed ? epdDD.dateOnFeed : this.epd.dateOnFeed
                this.epd.breedOfDam = epdDD.breedOfDam ? epdDD.breedOfDam : this.epd.breedOfDam
                this.epd.ribEyeArea = epdDD.ribEyeArea ? epdDD.ribEyeArea : this.epd.ribEyeArea
                this.epd.ribEyeArea_contemporary = epdDD.ribEyeArea_contemporary ? epdDD.ribEyeArea_contemporary : this.epd.ribEyeArea_contemporary
                this.epd.fatThickness = epdDD.fatThickness ? epdDD.fatThickness : this.epd.fatThickness
                this.epd.fatThickness_contemporary = epdDD.fatThickness_contemporary ? epdDD.fatThickness_contemporary : this.epd.fatThickness_contemporary
                this.epd.marbling = epdDD.marbling ? epdDD.marbling : this.epd.marbling
                this.epd.marblingContGrp = epdDD.marbling_contemporary ? epdDD.marbling_contemporary : this.epd.marblingContGrp

                this.epd.carcassWeight = epdDD.carcass_weight ? epdDD.carcass_weight : this.epd.carcass_weight
                this.epd.carcassWeightContGrp = epdDD.carcass_weight_contemporary ? epdDD.carcass_weight_contemporary : this.epd.carcass_weight_contemporary
                this.epd.kphFat = epdDD.kph_fat ? epdDD.kph_fat : this.epd.kphFat
                // this.epd.yieldGrade = epdDD.yield_grade?epdDD.yield_grade:this.epd.yield_grade
                this.epd.yieldGradeContGrp = epdDD.yield_grade_contemporary ? epdDD.yield_grade_contemporary : this.epd.yieldGradeContGrp
            }
        },
    },
    mixins: [loadingMixin]
}
</script>

<style scoped>
.expansion-header {
    border: 1px solid #FFB443 !important;
    background-color: #FFF7EC !important;
}

::v-deep .required-field .v-input__slot {
    border: 1px solid #FFB443 !important;
    background-color: #FFF7EC !important;
}

.expansion-close {
    background-color: #9695943a !important;
    border-radius: 0px !important;
}

.label {
    margin-bottom: 20px !important;
}

.v-application .subtitle-1 {
    font-size: 13px !important;
    font-weight: normal;
    letter-spacing: 0.009375em !important;
    line-height: 1.75rem;
    font-family: "Roboto", sans-serif !important;
}

.note {
    background-color: #FFF7EC !important;
}
</style>
